export type CatalogFilterKind = 'allergen' | 'tag';
export type CatalogFilterMode = 'exclude' | 'require';

/** One allergen or tag the customer can toggle in the catalog filter dialog. */
export interface CatalogFilterSelection {
  id: string;
  kind: CatalogFilterKind;
  mode: CatalogFilterMode;
  /** Raw allergen name (translated at render) or merchant tag name. */
  name: string;
}

export interface CatalogFilterOptions {
  /** Allergens, then tags whose filter mode hides products that contain them. */
  exclude: CatalogFilterSelection[];
  /** Tags whose filter mode keeps only products that have them. */
  require: CatalogFilterSelection[];
}

export interface CatalogFilterProduct {
  allergens?: { id?: string; name?: string }[];
  labels?: {
    id?: string;
    name?: string;
    filtrable_in_modal?: boolean;
    filter_mode?: string;
    position?: number;
  }[];
}

export interface CatalogFilterCategoryNode<TProduct> {
  products: TProduct[];
  children: CatalogFilterCategoryNode<TProduct>[];
}

/** Tag rendered next to an addition that fails an active filter. */
export interface CatalogFilterMatchTag {
  id: string;
  kind: CatalogFilterKind;
  name: string;
  /** Exclude matches get a warning icon. Require misses stay plain. */
  warning: boolean;
}

const FILTER_MODE_EXCLUDE = 'exclude';

export function catalogFilterKey(filter: CatalogFilterSelection): string {
  return `${filter.kind}:${filter.id}`;
}

export function hasCatalogFilterOptions(options: CatalogFilterOptions | undefined): boolean {
  if (!options) {
    return false;
  }
  return options.exclude.length > 0 || options.require.length > 0;
}

/**
 * Allergens and `filtrable_in_modal` tags that appear on at least one shop product.
 * Missing `filter_mode` follows the dashboard default (require / "Uniquement").
 */
export function collectCatalogFilterOptions(
  products: readonly CatalogFilterProduct[],
): CatalogFilterOptions {
  const allergens = new Map<string, CatalogFilterSelection>();
  const excludeTags = new Map<string, { selection: CatalogFilterSelection; position: number }>();
  const requireTags = new Map<string, { selection: CatalogFilterSelection; position: number }>();

  for (const product of products) {
    for (const allergen of product.allergens ?? []) {
      if (!allergen.id || !allergen.name || allergens.has(allergen.id)) {
        continue;
      }
      allergens.set(allergen.id, {
        id: allergen.id,
        kind: 'allergen',
        mode: 'exclude',
        name: allergen.name,
      });
    }

    for (const label of product.labels ?? []) {
      if (!label.filtrable_in_modal || !label.id || !label.name) {
        continue;
      }
      const mode: CatalogFilterMode =
        label.filter_mode === FILTER_MODE_EXCLUDE ? 'exclude' : 'require';
      const target = mode === 'exclude' ? excludeTags : requireTags;
      if (target.has(label.id)) {
        continue;
      }
      target.set(label.id, {
        position: label.position ?? 0,
        selection: {
          id: label.id,
          kind: 'tag',
          mode,
          name: label.name,
        },
      });
    }
  }

  const byName = (left: CatalogFilterSelection, right: CatalogFilterSelection) =>
    left.name.localeCompare(right.name);
  const byPosition = (
    left: { selection: CatalogFilterSelection; position: number },
    right: { selection: CatalogFilterSelection; position: number },
  ) => left.position - right.position || left.selection.name.localeCompare(right.selection.name);

  return {
    exclude: [
      ...[...allergens.values()].sort(byName),
      ...[...excludeTags.values()].sort(byPosition).map(entry => entry.selection),
    ],
    require: [...requireTags.values()].sort(byPosition).map(entry => entry.selection),
  };
}

function productHasFilter(product: CatalogFilterProduct, filter: CatalogFilterSelection): boolean {
  if (filter.kind === 'allergen') {
    return product.allergens?.some(allergen => allergen.id === filter.id) ?? false;
  }
  return product.labels?.some(label => label.id === filter.id) ?? false;
}

/**
 * Every selected filter applies (AND).
 * Exclude: the product must not contain the allergen or tag.
 * Require: the product must have the tag.
 */
export function productMatchesCatalogFilters(
  product: CatalogFilterProduct,
  filters: readonly CatalogFilterSelection[],
): boolean {
  return filters.every(filter => {
    const hasFilter = productHasFilter(product, filter);
    return filter.mode === 'exclude' ? !hasFilter : hasFilter;
  });
}

export function filterTreeByCatalogFilters<
  T extends CatalogFilterCategoryNode<CatalogFilterProduct>,
>(tree: readonly T[], filters: readonly CatalogFilterSelection[]): T[] {
  if (!filters.length) {
    return [...tree];
  }

  return tree
    .map(node => {
      const children = filterTreeByCatalogFilters(node.children as T[], filters);
      const products = node.products.filter(product =>
        productMatchesCatalogFilters(product, filters),
      );
      return { ...node, products, children };
    })
    .filter(node => node.products.length > 0 || node.children.length > 0);
}

export function countProductsInTree(tree: readonly CatalogFilterCategoryNode<unknown>[]): number {
  return tree.reduce(
    (total, node) => total + node.products.length + countProductsInTree(node.children ?? []),
    0,
  );
}

/**
 * Additions and formula choices stay visible. Each active filter they fail becomes a tag:
 * contained exclude allergen/tag → warning, missing require tag → plain name.
 */
export function catalogFilterMatchTags(
  product: CatalogFilterProduct,
  filters: readonly CatalogFilterSelection[],
): CatalogFilterMatchTag[] {
  const tags: CatalogFilterMatchTag[] = [];
  for (const filter of filters) {
    const hasFilter = productHasFilter(product, filter);
    if (filter.mode === 'exclude' && hasFilter) {
      tags.push({
        id: catalogFilterKey(filter),
        kind: filter.kind,
        name: filter.name,
        warning: true,
      });
    }
    if (filter.mode === 'require' && !hasFilter) {
      tags.push({
        id: catalogFilterKey(filter),
        kind: filter.kind,
        name: filter.name,
        warning: false,
      });
    }
  }
  return tags;
}
